import { Dimensions, Metrics } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';

// Grand-total metric value (rollup grouping 0).
export function readGrandTotal(rollups: AnalyticsResponseRollupCollection, metric: Metrics): number | null {
  const total = rollups.groupings[0];
  if (!total) return null;
  return total.metrics.getCurrentSeries(metric)[0] ?? null;
}

// Same as readGrandTotal but defaulting null to 0 — for count metrics that should never be null in UI.
export function readGrandTotalOrZero(rollups: AnalyticsResponseRollupCollection, metric: Metrics): number {
  return readGrandTotal(rollups, metric) ?? 0;
}

// Maps each rate metric to the underlying referral_status its rate is computed over. Used to
// recover an exact status-level count from the (entity, status) leaf groupings the slideout's
// main query already includes — avoiding back-calculating from a server-rounded rate.
const RATE_METRIC_STATUS: Partial<Record<Metrics, string>> = {
  [Metrics.ACCEPTANCE_RATE]: 'accepted',
  [Metrics.DENIAL_RATE]: 'rejected',
  [Metrics.CANCEL_RATE]: 'canceled',
  [Metrics.PENDING_RATE]: 'pending',
};

// Sum review_id:count across leaf rows (grouping=0) where referral_status matches. The rollup
// collection strips grouping=0 rows from its groupings list, so we walk the raw responseData.
export function readStatusCount(rollups: AnalyticsResponseRollupCollection, status: string): number {
  const response = rollups.analyticsResponse;
  const statusIdx = response.findDimensionIndex(Dimensions.REFERRAL_STATUS);
  const countIdx = response.findMetricIndex(Metrics.REVIEW_ID_COUNT);
  if (statusIdx < 0 || countIdx < 0) return 0;
  return response.responseData.reduce((sum, row) => {
    if (row.grouping !== 0 || row.datesIndex !== 0) return sum;
    if (row.dimensionValues[statusIdx]?.value !== status) return sum;
    const count = Number(row.metricValues[countIdx]?.value ?? 0);
    return Number.isFinite(count) ? sum + count : sum;
  }, 0);
}

// Combined read used by every metric-detail header: total referrals + a rate + the exact count.
export function readReferralRate(
  rollups: AnalyticsResponseRollupCollection,
  rateMetric: Metrics
): { rate: number | null; count: number; total: number } {
  const total = readGrandTotalOrZero(rollups, Metrics.REVIEW_ID_COUNT);
  const rate = readGrandTotal(rollups, rateMetric);
  const status = RATE_METRIC_STATUS[rateMetric];
  const count = status ? readStatusCount(rollups, status) : 0;
  return { rate, count, total };
}

// Zip dimension names + counts, drop empty / 'None' / zero-count rows, optionally remap display names,
// optionally sort by count desc. Used by the per-metric breakdown charts.
export function buildBreakdownRows(
  names: ReadonlyArray<string | null | undefined>,
  counts: ReadonlyArray<number | null | undefined>,
  options: { displayNameMap?: Record<string, string>; lowercase?: boolean; sortDesc?: boolean } = {}
): Array<{ name: string; count: number }> {
  const { displayNameMap, lowercase, sortDesc } = options;
  const rows = names
    .map((raw, i) => {
      const key = String(raw ?? '');
      const display = displayNameMap?.[key] ?? key;
      return { name: lowercase ? display.toLowerCase() : display, count: counts[i] ?? 0 };
    })
    .filter((r) => r.name && r.name.toLowerCase() !== 'none' && r.count > 0);
  if (sortDesc) rows.sort((a, b) => b.count - a.count);
  return rows;
}
